/**
 * AdvancedTicketGraphs — replaces the "Ticket Source Analysis" pie chart on
 * the Admin Analytics dashboards. Renders two automatically-generated
 * charts:
 *   1. Tickets by Problem Location vs Resolving Department (stacked bar).
 *   2. Tickets per Department vs Average Time to each Status update.
 *
 * Both charts are gated by a 20-second deliberate "analyzing" loader per
 * the product spec. The fetch starts immediately and the loader is held
 * for the configured minimum so users see a clear, predictable waiting
 * state. Errors are surfaced inline (no toast spam) and a Refresh button
 * triggers a manual re-analyze.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { analyticsAPI } from '@/lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import { RefreshCw, BarChart3, Activity, Sparkles } from 'lucide-react';

const ANALYSIS_DELAY_MS = 20000;

// Distinct, accessible palette for stacking departments by location.
// 18 swatches — covers the 11 Infra + 10 Admin + 9 Academic departments
// without forced repetition.
const DEPT_PALETTE = [
  '#E97832', '#0EA5E9', '#16A34A', '#9333EA', '#DC2626', '#0D9488',
  '#CA8A04', '#7C3AED', '#0284C7', '#65A30D', '#DB2777', '#0891B2',
  '#EA580C', '#4F46E5', '#059669', '#BE185D', '#1D4ED8', '#B45309',
];

const STATUS_COLORS = {
  Acknowledged: '#0EA5E9',
  'In Progress': '#F59E0B',
  'SLA Breach': '#DC2626',
  Resolved: '#16A34A',
};

const fmtH = (h) => (h === null || h === undefined ? '—' : `${h.toFixed(1)} h`);

const TopDeptsLimit = 12;
const TopLocationsLimit = 12;

const useAnalyzingTimer = (running) => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!running) { setProgress(0); return undefined; }
    setProgress(0);
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const next = Math.min(100, (elapsed / ANALYSIS_DELAY_MS) * 100);
      setProgress(next);
    }, 200);
    return () => clearInterval(id);
  }, [running]);
  return progress;
};

const AnalyzingBlock = ({ label }) => {
  const progress = useAnalyzingTimer(true);
  const remaining = Math.max(0, Math.ceil((ANALYSIS_DELAY_MS - (progress / 100) * ANALYSIS_DELAY_MS) / 1000));
  return (
    <div data-testid="analyzing-block" className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <Sparkles className="w-10 h-10 text-[#E97832] animate-pulse mb-3" />
      <p className="text-sm font-semibold text-slate-700">{label}</p>
      <p className="text-xs text-slate-500 mt-1">
        Crunching the numbers…{remaining > 0 ? ` ${remaining}s remaining` : ' wrapping up'}
      </p>
      <div className="w-full max-w-sm mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#E97832] to-[#EA580C] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const LocationVsDepartmentChart = ({ data }) => {
  // Convert flat matrix to recharts shape: one row per location, one key
  // per department. Limit to top-N locations + departments to keep the
  // chart readable.
  const { rows, departments } = useMemo(() => {
    if (!data) return { rows: [], departments: [] };
    const topLocations = data.locations.slice(0, TopLocationsLimit);
    const topDepts = data.departments.slice(0, TopDeptsLimit);
    const cellLookup = {};
    data.matrix.forEach((m) => {
      cellLookup[`${m.location}|${m.department}`] = m.count;
    });
    const rowsOut = topLocations.map((loc) => {
      const r = { location: loc };
      topDepts.forEach((d) => {
        r[d] = cellLookup[`${loc}|${d}`] || 0;
      });
      return r;
    });
    return { rows: rowsOut, departments: topDepts };
  }, [data]);

  if (!rows.length) {
    return <div className="text-center py-8 text-slate-400 text-sm">No location/department data yet.</div>;
  }

  return (
    <div data-testid="location-vs-department-chart">
      <ResponsiveContainer width="100%" height={Math.max(320, rows.length * 32 + 80)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis type="number" stroke="#64748b" allowDecimals={false} />
          <YAxis type="category" dataKey="location" stroke="#64748b" width={150} tick={{ fontSize: 11 }} />
          <Tooltip
            cursor={{ fill: '#f1f5f9' }}
            contentStyle={{ fontSize: 12, borderRadius: 6 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {departments.map((dept, i) => (
            <Bar key={dept} dataKey={dept} stackId="a" fill={DEPT_PALETTE[i % DEPT_PALETTE.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-slate-500 mt-2">
        Showing top {rows.length} locations × top {departments.length} departments. Hover any bar to see the exact ticket count.
      </p>
    </div>
  );
};

const DepartmentStatusTatChart = ({ data }) => {
  const rows = useMemo(() => {
    if (!data?.departments) return [];
    return data.departments.slice(0, TopDeptsLimit).map((d) => ({
      department: d.department,
      ticket_count: d.ticket_count,
      Acknowledged: d.avg_to_acknowledged_hours || 0,
      'In Progress': d.avg_to_in_progress_hours || 0,
      'SLA Breach': d.avg_to_breach_hours || 0,
      Resolved: d.avg_to_resolved_hours || 0,
    }));
  }, [data]);

  if (!rows.length) {
    return <div className="text-center py-8 text-slate-400 text-sm">No department TAT data yet.</div>;
  }

  return (
    <div data-testid="department-status-tat-chart">
      <ResponsiveContainer width="100%" height={Math.max(360, rows.length * 38 + 80)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            type="number"
            stroke="#64748b"
            label={{ value: 'Average hours since creation', position: 'insideBottom', offset: -2, fontSize: 11, fill: '#64748b' }}
          />
          <YAxis type="category" dataKey="department" stroke="#64748b" width={170} tick={{ fontSize: 11 }} />
          <Tooltip
            cursor={{ fill: '#f1f5f9' }}
            contentStyle={{ fontSize: 12, borderRadius: 6 }}
            formatter={(value, name) => [fmtH(value), name]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {Object.keys(STATUS_COLORS).map((k) => (
            <Bar key={k} dataKey={k} fill={STATUS_COLORS[k]}>
              {rows.map((row, idx) => <Cell key={`${k}-${idx}`} fill={STATUS_COLORS[k]} />)}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
        {rows.slice(0, 4).map((r) => (
          <div key={r.department} className="bg-slate-50 border border-slate-200 rounded p-2">
            <p className="font-semibold text-slate-700 truncate" title={r.department}>{r.department}</p>
            <p className="text-slate-500">Tickets: {r.ticket_count}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">
        Hours measured from ticket creation to each milestone. Empty bars mean the department has not yet reached that status. Showing top {rows.length} departments by volume.
      </p>
    </div>
  );
};

const AdvancedTicketGraphs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locVsDept, setLocVsDept] = useState(null);
  const [deptTat, setDeptTat] = useState(null);
  const lastFetchRef = useRef(0);

  const analyze = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    lastFetchRef.current = Date.now();
    try {
      // Fetch + minimum 20s analyzing window run in parallel.
      const minDelay = new Promise((resolve) => setTimeout(resolve, ANALYSIS_DELAY_MS));
      const [locRes, tatRes] = await Promise.all([
        analyticsAPI.getLocationVsDepartment(),
        analyticsAPI.getDepartmentStatusTat(),
      ]);
      await minDelay;
      setLocVsDept(locRes.data);
      setDeptTat(tatRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load advanced analytics. Click Refresh to retry.');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('AdvancedTicketGraphs failed:', e?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    analyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="advanced-ticket-graphs">
      {/* Chart 1 — Location vs Department */}
      <Card data-testid="location-vs-department-card">
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
          <div>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#E97832]" />
              Tickets by Problem Location vs Resolving Department
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Where do tickets originate, and which department picks them up? Bars stack each department's contribution per location.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={analyze}
            disabled={loading}
            data-testid="advanced-graphs-refresh"
            className="flex-shrink-0"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analyzing…' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <AnalyzingBlock label="Computing Location × Department matrix…" />
          ) : error ? (
            <div className="text-amber-700 text-sm border border-amber-300 bg-amber-50 rounded-md px-3 py-2">{error}</div>
          ) : (
            <LocationVsDepartmentChart data={locVsDept} />
          )}
        </CardContent>
      </Card>

      {/* Chart 2 — Department vs Avg Status-Update TAT */}
      <Card data-testid="dept-status-tat-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#E97832]" />
            Tickets per Department vs Average Time to Status Update
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            For each resolving department, the average hours from ticket creation to each status milestone (Acknowledged, In Progress, SLA Breach, Resolved).
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <AnalyzingBlock label="Computing average status-update TATs…" />
          ) : error ? (
            <div className="text-amber-700 text-sm border border-amber-300 bg-amber-50 rounded-md px-3 py-2">{error}</div>
          ) : (
            <DepartmentStatusTatChart data={deptTat} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedTicketGraphs;
